import React, { Children, isValidElement, useEffect, useRef } from 'react';
import type { FooterDockRowProps } from './FooterDockRow';
import { useBottomPinnedKeyboardOffset } from './useKeyboardPin';
import './footer.css';

/** How the dock's shared scrim is painted behind the rows. */
export type FooterDockBackground = 'gradient' | 'opaque' | 'transparent';

export interface FooterDockProps {
  /** A set of `FooterDockRow`s. At most one may have `pin="keyboard"`. */
  children: React.ReactNode;
  /**
   * Scrim style behind the rows:
   * - `'gradient'` (default) — a translucent, blurred gradient that fades the
   *   page out behind the dock.
   * - `'opaque'` — a solid panel, so the page doesn't bleed through. Use when the
   *   dock content is already visually busy (e.g. the compose field options).
   * - `'transparent'` — no scrim at all; the page background shows straight
   *   through behind the rows. Use when the dock sits on a plain page background
   *   and needs no fade/blur (e.g. the short Share Wizard confirm steps).
   */
  background?: FooterDockBackground;
}

/**
 * Stacks any number of `FooterDockRow`s and reconciles them with the on-screen
 * keyboard (PWA-only). The `pin="bottom"` ("bottom-pinned") rows are docked to
 * the screen bottom and measured at runtime; the single optional `pin="keyboard"`
 * row rests atop the bottom-pinned stack and reconciles itself with the keyboard
 * (see FooterDockRow / useKeyboardPin).
 *
 * Measurement (no hardcoded heights): a `ResizeObserver` watches the bottom-pinned
 * stack and publishes the keyboard row's resting bottom as `--footer-keyboard-rest`
 * on the dock container — `measured height + gap` so the keyboard row rests one
 * gap atop the stack, or `0` when there is no stack so the keyboard row sits at
 * the screen bottom and its scrim bleeds off the bottom edge. The keyboard row
 * reads it in CSS — no React re-render when the stack resizes.
 *
 * Scrim: the dock draws a single `.footer-dock-backdrop` behind every row while
 * the keyboard is closed. The same observer measures the bottom stack and the
 * keyboard row and publishes the scrim's resting height as `--footer-dock-rest-bg`
 * (full dock content + the top fade) and its collapsed height as
 * `--footer-dock-stack-bg` (just the bottom stack), which the CSS uses when the
 * keyboard row rises above the keyboard.
 */
export function FooterDock({
  children,
  background = 'gradient',
}: FooterDockProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // How far to translate the bottom-pinned stack DOWN so it stays behind the
  // keyboard on an Android-style layout resize (0 on iOS / when closed), honoring
  // the pin="bottom" contract. See useBottomPinnedKeyboardOffset and footer.css.
  const bottomPinnedOffset = useBottomPinnedKeyboardOffset();

  // Partition children by the typed `pin` prop — no type/prop sniffing of foreign
  // components, since `FooterDockRow` is ours.
  const rows = Children.toArray(children).filter(
    isValidElement
  ) as React.ReactElement<FooterDockRowProps>[];
  const keyboardRows = rows.filter(row => row.props.pin === 'keyboard');
  const bottomRows = rows.filter(row => row.props.pin !== 'keyboard');
  const hasBottomRows = bottomRows.length > 0;
  const hasKeyboardRow = keyboardRows.length > 0;

  if (import.meta.env.DEV && keyboardRows.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      'FooterDock: more than one pin="keyboard" row; using the first.'
    );
  }

  // Measure the bottom-pinned stack and the keyboard row → publish the keyboard
  // row's resting bottom (--footer-keyboard-rest) and the dock-wide scrim's resting
  // and collapsed heights (--footer-dock-rest-bg / --footer-dock-stack-bg) on the
  // container, so the (sibling) keyboard row and scrim position themselves via
  // custom-property inheritance — no React re-render when a row resizes.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const bottomEl = bottomRef.current;
    const keyboardEl = container.querySelector<HTMLElement>(
      '.footer-dock-row--keyboard'
    );

    const publish = () => {
      const stack = bottomEl?.offsetHeight ?? 0;
      const keyboard = keyboardEl?.offsetHeight ?? 0;
      // Keyboard row rests one gap atop the stack, or at the screen bottom (0)
      // when there is no stack so its scrim bleeds off the edge.
      container.style.setProperty(
        '--footer-keyboard-rest',
        stack ? `calc(${stack}px + var(--footer-dock-gap))` : '0px'
      );
      // The dock-wide scrim spans the full dock content while the keyboard is
      // closed: the bottom stack, the inter-row gap (only when both rows exist),
      // the keyboard row, plus the fade above.
      const content =
        stack && keyboard
          ? `calc(${stack}px + var(--footer-dock-gap) + ${keyboard}px)`
          : keyboard
            ? `${keyboard}px`
            : `${stack}px`;
      container.style.setProperty(
        '--footer-dock-rest-bg',
        `calc(${content} + var(--footer-dock-backdrop-fade))`
      );
      // Collapsed height (keyboard up): just the bottom stack, hidden behind the
      // keyboard while the keyboard row floats above with its own scrim.
      container.style.setProperty(
        '--footer-dock-stack-bg',
        `calc(${stack}px + var(--footer-dock-backdrop-fade))`
      );
    };
    publish();

    // Rows can change height from conditional content, font scaling, or
    // orientation changes — observe, don't one-shot measure.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(publish);
    if (bottomEl) ro.observe(bottomEl);
    if (keyboardEl) ro.observe(keyboardEl);
    return () => ro.disconnect();
  }, [hasBottomRows, hasKeyboardRow]);

  return (
    // A zero-box container that owns the dock's custom properties; its fixed
    // children escape flow, so it adds no layout. (Only the fixed children may be
    // translated — a transform here would become their containing block and break
    // position: fixed.)
    <div
      ref={containerRef}
      className={
        'footer-dock' +
        (background === 'opaque'
          ? ' footer-dock--opaque'
          : background === 'transparent'
            ? ' footer-dock--transparent'
            : '')
      }
      style={
        {
          // Published for the bottom-pinned stack + scrim to read (translateY),
          // keeping them behind the keyboard on an Android layout resize.
          '--footer-bottom-pinned-offset': `${bottomPinnedOffset}px`,
          // No-stack value set inline too, so the keyboard row sits at the bottom
          // from the first paint (no flash) before the effect measures.
          ...(hasBottomRows ? null : { '--footer-keyboard-rest': '0px' }),
        } as React.CSSProperties
      }
    >
      {/* The keyboard row comes first so a sibling combinator off its
          data-kb-active state can drive both the bottom stack (hidden when the
          keyboard is up) and the scrim (collapsed) below. Visual stacking is set
          by explicit z-index, not DOM order. */}
      {keyboardRows[0] ?? null}
      {hasBottomRows ? (
        <div ref={bottomRef} className="footer-dock-bottom-pinned">
          {bottomRows}
        </div>
      ) : null}
      {/* Single dock-wide scrim, drawn behind every row (z-index 19 < 20). */}
      <div aria-hidden="true" className="footer-dock-backdrop" />
    </div>
  );
}

export default FooterDock;
